import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, Article, Robot, Gear, Trash, Eye, EyeSlash,
  Lightning, Clock, ChartLine, Warning, Check, Play, Pause,
  UserPlus, ChatCircle, CaretRight, Info, Sparkle, Calendar
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSeedingPage = () => {
  const { isAdmin, getAuthHeader, user } = useAuth();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [aiAssistants, setAiAssistants] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Form state
  const [memberCount, setMemberCount] = useState(50);
  const [postCount, setPostCount] = useState(100);
  const [daysBack, setDaysBack] = useState(14);
  const [dailyPosts, setDailyPosts] = useState(3);
  const [dailyComments, setDailyComments] = useState(15);
  
  // Check for admin token from admin dashboard
  const adminToken = localStorage.getItem('powr-admin-token');

  // Get auth headers - prefer admin token if available
  const getHeaders = () => {
    if (adminToken) {
      return { Authorization: `Bearer ${adminToken}` };
    }
    return getHeaders();
  };

  useEffect(() => {
    // Allow access if user is admin OR has admin token
    if (!isAdmin && !adminToken) {
      navigate('/community');
      return;
    }
    setIsAuthenticated(true);
    fetchStatus();
    fetchAiAssistants();
  }, [isAdmin, adminToken, navigate]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/seeding/status`, {
        headers: getHeaders()
      });
      setStatus(res.data);
      setDailyPosts(res.data.daily_posts || 3);
      setDailyComments(res.data.daily_comments || 15);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchAiAssistants = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/seeding/ai-assistants`, {
        headers: getHeaders()
      });
      setAiAssistants(res.data);
    } catch (error) {
    }
  };

  const handleGenerateMembers = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/seeding/generate-members?count=${memberCount}`,
        {},
        { headers: getHeaders() }
      );
      toast.success(res.data.message);
      fetchStatus();
    } catch (error) {
      toast.error('Failed to generate members');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateContent = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/seeding/generate-content?post_count=${postCount}&days_back=${daysBack}`,
        {},
        { headers: getHeaders() }
      );
      toast.success(res.data.message);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleVisibility = async () => {
    try {
      const newState = !status.demo_content_visible;
      await axios.post(
        `${API}/api/admin/seeding/toggle?enabled=${newState}`,
        {},
        { headers: getHeaders() }
      );
      toast.success(`Demo content ${newState ? 'shown' : 'hidden'}`);
      fetchStatus();
    } catch (error) {
      toast.error('Failed to toggle visibility');
    }
  };

  const handleToggleAutoSeeding = async () => {
    try {
      const newState = !status.auto_seeding_enabled;
      await axios.post(
        `${API}/api/admin/seeding/auto-seeding?enabled=${newState}&daily_posts=${dailyPosts}&daily_comments=${dailyComments}`,
        {},
        { headers: getHeaders() }
      );
      toast.success(`Auto-seeding ${newState ? 'enabled' : 'disabled'}`);
      fetchStatus();
    } catch (error) {
      toast.error('Failed to toggle auto-seeding');
    }
  };

  const handleClearContent = async () => {
    if (!window.confirm('Are you sure you want to delete ALL demo content? This cannot be undone.')) {
      return;
    }
    
    setGenerating(true);
    try {
      const res = await axios.delete(`${API}/api/admin/seeding/clear`, {
        headers: getHeaders()
      });
      toast.success(res.data.message);
      fetchStatus();
    } catch (error) {
      toast.error('Failed to clear content');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C4A661] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-[#1a1a1a] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/admin-dashboard')}
                className="text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#F7F7F5]">Community Seeding System</h1>
                <p className="text-sm text-[#A3A3A3]">Ethical content generation with full transparency</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                status?.is_active 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-[#262626] text-[#A3A3A3]'
              }`}>
                {status?.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Transparency Notice */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-[#C4A661]/10 to-[#8B5CF6]/10 border border-[#C4A661]/30 rounded-xl p-5 mb-8"
        >
          <div className="flex items-start gap-4">
            <Info size={24} className="text-[#C4A661] flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-[#F7F7F5] font-bold mb-1">Ethical Community Seeding</h3>
              <p className="text-[#A3A3A3] text-sm">
                All demo content is clearly labeled as "Sample Discussion" or "Demo Member" to maintain full transparency with your community. 
                AI assistants are labeled as AI. This builds trust while creating an engaging experience for new members.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={Users} 
            label="Demo Members" 
            value={status?.demo_members_count || 0}
            color="#C4A661"
          />
          <StatCard 
            icon={Article} 
            label="Demo Posts" 
            value={status?.demo_posts_count || 0}
            color="#8B5CF6"
          />
          <StatCard 
            icon={Robot} 
            label="AI Posts Today" 
            value={status?.ai_posts_today || 0}
            color="#10B981"
          />
          <StatCard 
            icon={Clock} 
            label="Last Seeded" 
            value={status?.last_seeded ? new Date(status.last_seeded).toLocaleDateString() : 'Never'}
            color="#F59E0B"
            isText
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Demo Member Generator */}
            <div className="bg-[#141414] border border-[#262626] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#C4A661]/20 flex items-center justify-center">
                  <UserPlus size={20} className="text-[#C4A661]" />
                </div>
                <div>
                  <h3 className="text-[#F7F7F5] font-bold">Demo Member Generator</h3>
                  <p className="text-xs text-[#A3A3A3]">Generate realistic member profiles</p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm text-[#A3A3A3] mb-2">Number of Members (25-100)</label>
                <input
                  type="number"
                  value={memberCount}
                  onChange={(e) => setMemberCount(Math.max(25, Math.min(100, parseInt(e.target.value) || 50)))}
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3 text-[#F7F7F5] focus:outline-none focus:border-[#C4A661]"
                />
              </div>
              
              <Button 
                onClick={handleGenerateMembers}
                disabled={generating}
                className="w-full btn-gold"
              >
                {generating ? 'Generating...' : 'Generate Members'}
              </Button>
              
              <p className="text-xs text-[#525252] mt-3">
                Members labeled: "Demo Member • Example Profile"
              </p>
            </div>

            {/* Content Seeding Engine */}
            <div className="bg-[#141414] border border-[#262626] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
                  <Article size={20} className="text-[#8B5CF6]" />
                </div>
                <div>
                  <h3 className="text-[#F7F7F5] font-bold">AI Content Seeding Engine</h3>
                  <p className="text-xs text-[#A3A3A3]">Generate posts with comments across categories</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">Posts (50-200)</label>
                  <input
                    type="number"
                    value={postCount}
                    onChange={(e) => setPostCount(Math.max(50, Math.min(200, parseInt(e.target.value) || 100)))}
                    className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3 text-[#F7F7F5] focus:outline-none focus:border-[#C4A661]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A3A3A3] mb-2">Days Back (7-30)</label>
                  <input
                    type="number"
                    value={daysBack}
                    onChange={(e) => setDaysBack(Math.max(7, Math.min(30, parseInt(e.target.value) || 14)))}
                    className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3 text-[#F7F7F5] focus:outline-none focus:border-[#C4A661]"
                  />
                </div>
              </div>
              
              <Button 
                onClick={handleGenerateContent}
                disabled={generating || status?.demo_members_count === 0}
                className="w-full btn-gold"
              >
                {generating ? 'Generating...' : 'Generate Content'}
              </Button>
              
              {status?.demo_members_count === 0 && (
                <p className="text-xs text-[#F59E0B] mt-3">
                  ⚠️ Generate demo members first
                </p>
              )}
              
              <div className="mt-4 pt-4 border-t border-[#262626]">
                <p className="text-xs text-[#A3A3A3] mb-2">Categories covered:</p>
                <div className="flex flex-wrap gap-2">
                  {['Faith', 'Wealth', 'Business', 'Brotherhood', 'Mindset'].map(cat => (
                    <span key={cat} className="px-2 py-1 bg-[#262626] rounded text-xs text-[#A3A3A3]">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Admin Controls */}
            <div className="bg-[#141414] border border-[#262626] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <Gear size={20} className="text-[#10B981]" />
                </div>
                <div>
                  <h3 className="text-[#F7F7F5] font-bold">Admin Controls</h3>
                  <p className="text-xs text-[#A3A3A3]">Toggle and manage demo content</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Visibility Toggle */}
                <div className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg">
                  <div className="flex items-center gap-3">
                    {status?.demo_content_visible ? (
                      <Eye size={20} className="text-[#C4A661]" />
                    ) : (
                      <EyeSlash size={20} className="text-[#525252]" />
                    )}
                    <div>
                      <p className="text-sm text-[#F7F7F5] font-medium">Demo Content Visibility</p>
                      <p className="text-xs text-[#A3A3A3]">
                        {status?.demo_content_visible ? 'Visible to all users' : 'Hidden from users'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleVisibility}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      status?.demo_content_visible ? 'bg-[#C4A661]' : 'bg-[#262626]'
                    }`}
                  >
                    <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${
                      status?.demo_content_visible ? 'left-6' : 'left-0.5'
                    }`} />
                  </button>
                </div>

                {/* Auto-Seeding Toggle */}
                <div className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg">
                  <div className="flex items-center gap-3">
                    {status?.auto_seeding_enabled ? (
                      <Play size={20} className="text-[#10B981]" />
                    ) : (
                      <Pause size={20} className="text-[#525252]" />
                    )}
                    <div>
                      <p className="text-sm text-[#F7F7F5] font-medium">Auto-Seeding</p>
                      <p className="text-xs text-[#A3A3A3]">
                        {status?.auto_seeding_enabled ? 'Generating daily content' : 'Manual mode'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleAutoSeeding}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      status?.auto_seeding_enabled ? 'bg-[#10B981]' : 'bg-[#262626]'
                    }`}
                  >
                    <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${
                      status?.auto_seeding_enabled ? 'left-6' : 'left-0.5'
                    }`} />
                  </button>
                </div>

                {/* Daily Targets (when auto-seeding enabled) */}
                {status?.auto_seeding_enabled && (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-[#0A0A0A] rounded-lg">
                    <div>
                      <label className="block text-xs text-[#A3A3A3] mb-1">Daily Posts</label>
                      <input
                        type="number"
                        value={dailyPosts}
                        onChange={(e) => setDailyPosts(parseInt(e.target.value) || 3)}
                        className="w-full bg-[#141414] border border-[#262626] rounded px-3 py-2 text-sm text-[#F7F7F5]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[#A3A3A3] mb-1">Daily Comments</label>
                      <input
                        type="number"
                        value={dailyComments}
                        onChange={(e) => setDailyComments(parseInt(e.target.value) || 15)}
                        className="w-full bg-[#141414] border border-[#262626] rounded px-3 py-2 text-sm text-[#F7F7F5]"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* AI Community Assistants */}
            <div className="bg-[#141414] border border-[#262626] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                  <Robot size={20} className="text-[#F59E0B]" />
                </div>
                <div>
                  <h3 className="text-[#F7F7F5] font-bold">AI Community Assistants</h3>
                  <p className="text-xs text-[#A3A3A3]">4 specialized AI roles (clearly labeled)</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {aiAssistants.map(assistant => (
                  <div key={assistant.id} className="flex items-center gap-3 p-3 bg-[#0A0A0A] rounded-lg">
                    <img 
                      src={assistant.avatar} 
                      alt={assistant.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-[#F7F7F5] font-medium">{assistant.name}</p>
                        <span className="px-1.5 py-0.5 bg-[#8B5CF6]/20 text-[#8B5CF6] text-[10px] font-bold rounded">
                          AI
                        </span>
                      </div>
                      <p className="text-xs text-[#A3A3A3]">{assistant.role}</p>
                    </div>
                    <ChartLine size={16} className="text-[#525252]" />
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-[#525252] mt-4">
                All AI assistants display "AI" badge on their profiles and posts
              </p>
            </div>

            {/* Onboarding Flow Preview */}
            <div className="bg-[#141414] border border-[#262626] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#EC4899]/20 flex items-center justify-center">
                  <Sparkle size={20} className="text-[#EC4899]" />
                </div>
                <div>
                  <h3 className="text-[#F7F7F5] font-bold">Onboarding Flow</h3>
                  <p className="text-xs text-[#A3A3A3]">New member experience</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-[#0A0A0A] rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#C4A661]/20 flex items-center justify-center text-sm font-bold text-[#C4A661]">1</div>
                  <p className="text-sm text-[#A3A3A3]">Welcome message with getting started steps</p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-[#0A0A0A] rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#C4A661]/20 flex items-center justify-center text-sm font-bold text-[#C4A661]">2</div>
                  <p className="text-sm text-[#A3A3A3]">Suggested posts to engage with</p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-[#0A0A0A] rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#C4A661]/20 flex items-center justify-center text-sm font-bold text-[#C4A661]">3</div>
                  <p className="text-sm text-[#A3A3A3]">Suggested members to connect with</p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-[#0A0A0A] rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#C4A661]/20 flex items-center justify-center text-sm font-bold text-[#C4A661]">4</div>
                  <p className="text-sm text-[#A3A3A3]">Prompt for first interaction</p>
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-[#141414] border border-red-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Warning size={20} className="text-red-500" />
                </div>
                <div>
                  <h3 className="text-red-400 font-bold">Danger Zone</h3>
                  <p className="text-xs text-[#A3A3A3]">Destructive actions</p>
                </div>
              </div>
              
              <Button 
                onClick={handleClearContent}
                disabled={generating}
                className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
              >
                <Trash size={18} className="mr-2" />
                Clear All Demo Content
              </Button>
              
              <p className="text-xs text-[#525252] mt-3">
                This will permanently delete all demo members and seeded posts
              </p>
            </div>
          </div>
        </div>

        {/* Transparency Disclaimer */}
        <div className="mt-8 p-6 bg-[#141414] border border-[#262626] rounded-xl">
          <h3 className="text-[#F7F7F5] font-bold mb-3 flex items-center gap-2">
            <Check size={18} className="text-[#10B981]" />
            Transparency Features
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-[#0A0A0A] rounded-lg">
              <p className="text-sm text-[#F7F7F5] font-medium mb-1">Demo Member Badge</p>
              <p className="text-xs text-[#A3A3A3]">"Demo Member • Example Profile" shown on all demo profiles</p>
            </div>
            <div className="p-4 bg-[#0A0A0A] rounded-lg">
              <p className="text-sm text-[#F7F7F5] font-medium mb-1">Sample Post Label</p>
              <p className="text-xs text-[#A3A3A3]">"Sample Discussion" badge on all seeded posts</p>
            </div>
            <div className="p-4 bg-[#0A0A0A] rounded-lg">
              <p className="text-sm text-[#F7F7F5] font-medium mb-1">AI Badge</p>
              <p className="text-xs text-[#A3A3A3]">Clear "AI" indicator on all AI assistant content</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color, isText = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-[#141414] border border-[#262626] rounded-xl p-5"
  >
    <div className="flex items-center gap-3">
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">{label}</p>
        <p className={`font-bold ${isText ? 'text-sm' : 'text-2xl'} text-[#F7F7F5]`}>{value}</p>
      </div>
    </div>
  </motion.div>
);

export default AdminSeedingPage;
